import { useCallback, useEffect, useState } from 'react';
import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { getFromStorage, saveToStorage } from './storage';

// The user's own choices, by column name. Columns the user hasn't changed
// follow the defaults.
type Kolonnevalg = Record<string, boolean>;

/**
 * Remembers which columns the user shows in a MUIDataTable, across renders and
 * visits.
 *
 * mui-datatables' own storageKey doesn't do this for columns with `display`
 * set: that value always wins over the stored one, both when the page is
 * opened and every time the table gets new columns. Here every column gets
 * `display` from the user's choice instead, or from the defaults.
 *
 * @param lagringsnokkel Key in localStorage for the choices
 * @param standardSkjulte Columns hidden until the user shows them. Keep the
 * array stable, e.g. as a constant, so the columns aren't rebuilt each render.
 */
export const useKolonnevalg = (
    lagringsnokkel: string,
    standardSkjulte: ReadonlyArray<string> = [],
) => {
    const [valg, setValg] = useState<Kolonnevalg>(
        () => getFromStorage<Kolonnevalg>(lagringsnokkel) ?? {},
    );

    useEffect(() => {
        saveToStorage(lagringsnokkel, valg);
    }, [lagringsnokkel, valg]);

    /** The columns with `display` from the user's choices or the defaults. */
    const medKolonnevalg = useCallback(
        (kolonner: Array<MUIDataTableColumn>): Array<MUIDataTableColumn> =>
            kolonner.map((kolonne) => {
                // Not a choice for the user, e.g. columns only used for data
                if (kolonne.options?.display === 'excluded') return kolonne;

                const synlig =
                    valg[kolonne.name] ??
                    !standardSkjulte.includes(kolonne.name);
                return {
                    ...kolonne,
                    options: { ...kolonne.options, display: synlig },
                };
            }),
        [valg, standardSkjulte],
    );

    /** For the table's onViewColumnsChange, when the user shows or hides a column. */
    const onViewColumnsChange = useCallback<
        NonNullable<MUIDataTableOptions['onViewColumnsChange']>
    >((kolonne, handling) => {
        if (handling !== 'add' && handling !== 'remove') return;
        setValg((forrige) => ({ ...forrige, [kolonne]: handling === 'add' }));
    }, []);

    return { medKolonnevalg, onViewColumnsChange };
};
